import type { TooltipDataItem } from './types';
import type { PeriodData, Period } from '../../../types/portfolioDividend';
import { formatCurrencyToScaleTruncated } from '@/utilities/portfolio/formatValue';
import { formatterPercentage } from '@/utilities/format';
import { pluralise } from '@/utilities/pluralise';

const TOTAL_TITLE_BY_PERIOD: Record<Period, string> = {
  last1y: 'Last 12m Total',
  next1y: 'Next 12m Total',
  next2y: 'Next 2y Total',
  next3y: 'Next 3y Total',
};

const getHoldingText = (count: number | undefined | null) => {
  if (count === undefined || count === null) {
    return '';
  }
  return `${count} ${pluralise(count, 'holding', 'holdings')}`;
};

type TooltipContentParams = {
  data: PeriodData | undefined;
  currencyISO: string;
  selectedPeriod: Period;
};

export const getTooltipContent = ({
  data,
  currencyISO,
  selectedPeriod,
}: TooltipContentParams): TooltipDataItem[] => {
  if (!data) return [];

  const { lowScore, mediumScore, highScore, totalPayment } = data;

  return [
    {
      title: TOTAL_TITLE_BY_PERIOD[selectedPeriod],
      value: formatCurrencyToScaleTruncated(totalPayment, currencyISO),
    },
    {
      title: 'Low score 0-2',
      value: formatCurrencyToScaleTruncated(
        lowScore?.totalPayment,
        currencyISO
      ),
      ratio: formatterPercentage(lowScore?.totalContribution),
      holdingsText: getHoldingText(lowScore?.totalHoldingCount),
      valueColor: 'bad',
    },
    {
      title: 'Medium score 3-4',
      value: formatCurrencyToScaleTruncated(
        mediumScore?.totalPayment,
        currencyISO
      ),
      ratio: formatterPercentage(mediumScore?.totalContribution),
      holdingsText: getHoldingText(mediumScore?.totalHoldingCount),
      valueColor: 'warning',
    },
    {
      title: 'High score 5-6',
      value: formatCurrencyToScaleTruncated(
        highScore?.totalPayment,
        currencyISO
      ),
      ratio: formatterPercentage(highScore?.totalContribution),
      holdingsText: getHoldingText(highScore?.totalHoldingCount),
      valueColor: 'good',
    },
  ];
};
